import React, { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import DefaultLayout from '../components/layouts/DefaultLayout'
import {
  Box,
  Text,
  Container,
  Heading,
  VStack,
  HStack,
  SimpleGrid,
  useBreakpointValue,
  keyframes,
  Image,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  Button,
  useDisclosure,
  Divider,
  SimpleGrid as SimpleGridModal,
} from '@chakra-ui/react'
import { useNavigate } from 'react-router-dom'
import RegionMap from '../components/home/RegionMap'
import { getPopupNotice } from '../services/apiService'

// 애니메이션 키프레임
const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`

const fadeInScale = keyframes`
  from {
    opacity: 0;
    transform: scale(0.9);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
`

const float = keyframes`
  0%, 100% {
    transform: translateY(0px);
  }
  50% {
    transform: translateY(-20px);
  }
`

const gradient = keyframes`
  0% {
    background-position: 0% 50%;
  }
  50% {
    background-position: 100% 50%;
  }
  100% {
    background-position: 0% 50%;
  }
`

const pulse = keyframes`
  0%, 100% {
    opacity: 1;
  }
  50% {
    opacity: 0.7;
  }
`

const Home: React.FC = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const isMobile = useBreakpointValue({ base: true, md: false })
  const [isVisible, setIsVisible] = useState(false)
  const heroRef = useRef<HTMLDivElement>(null)
  const statsRef = useRef<HTMLDivElement>(null)
  const storyVideoRef = useRef<HTMLVideoElement>(null)
  const [isVideoMuted, setIsVideoMuted] = useState(true)
  
  // 통계 카운트업 상태
  const [counts, setCounts] = useState([0, 0, 0, 0])
  const [hasAnimated, setHasAnimated] = useState(false)
  
  // 팝업 공지 상태
  const { isOpen: isPopupOpen, onOpen: onPopupOpen, onClose: onPopupClose } = useDisclosure()
  const { isOpen: isImageOpen, onOpen: onImageOpen, onClose: onImageClose } = useDisclosure()
  const [popupNotice, setPopupNotice] = useState<any>(null)
  const [selectedImage, setSelectedImage] = useState<string | null>(null)

  const handleVideoToggleAudio = () => {
    if (storyVideoRef.current) {
      const nextMuted = !isVideoMuted
      setIsVideoMuted(nextMuted)
      storyVideoRef.current.muted = nextMuted
      if (!storyVideoRef.current.paused) {
        storyVideoRef.current.play().catch(() => {})
      } else {
        storyVideoRef.current.play().catch(() => {})
      }
    }
  }

  useEffect(() => {
    setIsVisible(true)
  }, [])

  // 팝업 공지 조회
  useEffect(() => {
    const fetchPopupNotice = async () => {
      try {
        const { data: res } = await getPopupNotice()
        if (res.statusCode === 200 && res.data) {
          // 오늘 하루 보지 않기 체크
          const hideUntil = localStorage.getItem('popupNoticeHideUntil')
          if (hideUntil) {
            const hideUntilDate = new Date(hideUntil)
            const now = new Date()
            if (now < hideUntilDate) {
              // 아직 숨김 기간이면 표시하지 않음
              return
            }
          }
          
          // 마지막으로 본 팝업 ID 체크
          const lastPopupId = localStorage.getItem('lastPopupNoticeId')
          if (lastPopupId !== String(res.data.id)) {
            setPopupNotice(res.data)
            onPopupOpen()
          }
        }
      } catch (error) {
        console.error('팝업 공지 조회 실패:', error)
      }
    }
    fetchPopupNotice()
  }, [onPopupOpen])

  // 통계 카운트업 애니메이션
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !hasAnimated) {
            setHasAnimated(true)
            
            const targets = [44, 100, 500, 2024]
            const duration = 3000 // 3초
            const startTime = Date.now()
            
            const animate = () => {
              const elapsed = Date.now() - startTime
              const progress = Math.min(elapsed / duration, 1)
              
              // easeOutCubic 함수로 부드러운 애니메이션
              const easeOutCubic = (t: number) => 1 - Math.pow(1 - t, 3)
              const easedProgress = easeOutCubic(progress)
              
              setCounts(
                targets.map((target) => Math.floor(target * easedProgress))
              )
              
              if (progress < 1) {
                requestAnimationFrame(animate)
              } else {
                setCounts(targets)
              }
            }
            
            requestAnimationFrame(animate)
          }
        })
      },
      { threshold: 0.3 }
    )

    if (statsRef.current) {
      observer.observe(statsRef.current)
    }

    return () => {
      if (statsRef.current) {
        observer.unobserve(statsRef.current)
      }
    }
  }, [hasAnimated])

  return (
    <DefaultLayout>
      <Box position="relative" overflow="hidden">
        {/* 히어로 섹션 */}
        <Box
          ref={heroRef}
          position="relative"
          minH="100vh"
          display="flex"
          alignItems="center"
          justifyContent="center"
          overflow="hidden"
        >
          {/* 백그라운드 비디오 */}
          <Box
            position="absolute"
            top="0"
            left="0"
            w="100%"
            h="100%"
            zIndex={0}
            overflow="hidden"
          >
            <Box
              as="video"
              position="absolute"
              top="50%"
              left="50%"
              transform="translate(-50%, -50%)"
              minW="100%"
              minH="100%"
              w="auto"
              h="auto"
              objectFit="cover"
              autoPlay
              loop
              muted
              playsInline
              src="/videos/ai.mp4"
              opacity={0.7}
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
              }}
            />
          </Box>

          {/* 배경 애니메이션 요소들 */}
          <Box
            position="absolute"
            top="10%"
            left="10%"
            w="300px"
            h="300px"
            borderRadius="full"
            bg="brand.500"
            opacity="0.1"
            filter="blur(60px)"
            animation={`${float} 6s ease-in-out infinite`}
            zIndex={1}
          />
          <Box
            position="absolute"
            bottom="20%"
            right="15%"
            w="400px"
            h="400px"
            borderRadius="full"
            bg="orange.400"
            opacity="0.1"
            filter="blur(80px)"
            animation={`${float} 8s ease-in-out infinite reverse`}
            zIndex={1}
          />

          {/* 메인 콘텐츠 */}
          <Container maxW="container.xl" position="relative" zIndex={2}>
            <VStack spacing={8} textAlign="center" py={20}>
              {/* 메인 타이틀 */}
              <Box
                animation={isVisible ? `${fadeInUp} 1s ease-out` : 'none'}
                opacity={isVisible ? 1 : 0}
              >
                <Box
                  p={{ base: 4, md: 6 }}
                  mb={6}
                >
                  <Heading
                    as="h1"
                    fontSize={{ base: '4xl', md: '6xl', lg: '7xl' }}
                    fontWeight="extrabold"
                    lineHeight="1.3"
                  >
                    <Text
                      as="span"
                      bgGradient="linear(to-r, orange.500, orange.400, orange.300, orange.400, orange.500)"
                      bgClip="text"
                      backgroundSize="200% auto"
                      animation={`${gradient} 3s ease infinite`}
                      color="transparent"
                      filter="drop-shadow(2px 2px 4px rgba(0, 0, 0, 0.8)) drop-shadow(0 0 8px rgba(0, 0, 0, 0.6))"
                      style={{
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                      }}
                    >
                      우리 땅, 함께 지키고 함께 성장합니다.
                    </Text>
                  </Heading>
                  <Text
                    fontSize={{ base: 'sm', md: 'md', lg: 'lg' }}
                    color="white"
                    mt={4}
                    fontWeight="500"
                    textShadow="1px 1px 4px rgba(0, 0, 0, 0.8), 0 0 10px rgba(0, 0, 0, 0.5)"
                  >
                    대한민국 최초 실물 기반 공동투자 & 공유지분 투명하고 합법적인 부동산 경매 개발 시스템 구축.
                  </Text>
                </Box>
              </Box>

              {/* CTA 버튼 */}
              <Box
                animation={isVisible ? `${fadeInScale} 1s ease-out 0.8s both` : 'none'}
                opacity={isVisible ? 1 : 0}
                pt={8}
                pb={20}
              >
                <HStack spacing={4} justify="center" flexWrap="wrap">
                  <Box
                    as="button"
                    px={8}
                    py={4}
                    bg="orange.500"
                    color="white"
                    borderRadius="full"
                    fontSize="lg"
                    fontWeight="700"
                    _hover={{
                      bg: 'orange.400',
                      transform: 'translateY(-2px)',
                      boxShadow: '0 10px 30px rgba(246, 192, 25, 0.4)',
                    }}
                    _active={{
                      transform: 'translateY(0)',
                    }}
                    transition="all 0.3s ease"
                    boxShadow="0 4px 15px rgba(246, 192, 25, 0.3)"
                    onClick={() => navigate('/business-introduction')}
                  >
                    사업 소개 보기
                  </Box>
                  <Box
                    as="button"
                    px={8}
                    py={4}
                    bg="transparent"
                    color="white"
                    borderRadius="full"
                    fontSize="lg"
                    fontWeight="700"
                    border="2px solid white"
                    _hover={{
                      bg: 'rgba(255,255,255,0.1)',
                      transform: 'translateY(-2px)',
                    }}
                    _active={{
                      transform: 'translateY(0)',
                    }}
                    transition="all 0.3s ease"
                    onClick={() => navigate('/introduction')}
                  >
                    회사 소개 보기
                  </Box>
                </HStack>
              </Box>
            </VStack>
          </Container>

          {/* 스크롤 인디케이터 */}
          <Box
            position="absolute"
            bottom="20px"
            left="50%"
            transform="translateX(-50%)"
            animation={`${float} 2s ease-in-out infinite`}
            zIndex={2}
          >
            <VStack spacing={2} color="white" opacity="0.7">
              <Text fontSize="xs" fontWeight="500">
                Scroll
              </Text>
              <Box
                w="2px"
                h="30px"
                bg="white"
                borderRadius="full"
                opacity="0.5"
              />
            </VStack>
          </Box>
        </Box>

        {/* 비디오 섹션 */}
        <Box
          as="section"
          position="relative"
          w="100%"
          py={16}
          bg="gray.50"
          overflow="hidden"
        >
          <Container maxW="container.xl">
            <VStack spacing={8}>
              {/* 섹션 헤더 */}
              <Box textAlign="center" maxW="800px" mx="auto">
                <Heading
                  as="h2"
                  fontSize={{ base: '2xl', md: '3xl', lg: '4xl' }}
                  fontWeight="800"
                  color="gray.800"
                  mb={4}
                >
                  토돌이가 전하는 마음
                </Heading>
                <Text
                  fontSize={{ base: 'md', md: 'lg' }}
                  color="gray.600"
                  lineHeight="tall"
                >
                  조용히 스며드는 아침 햇살처럼, 작은 씨앗 하나가 세상 속에 떨어졌어요
                  <br />
                  그 씨앗은 희망이었고, 누군가의 손길이 닿을 때마다 조금씩, 조금씩 자라났죠
                </Text>
              </Box>

              {/* 비디오 */}
              <Box
                w="100%"
                maxW="740px"
                mx="auto"
                position="relative"
                borderRadius="2xl"
                overflow="hidden"
                boxShadow="2xl"
                bg="gray.900"
                cursor="pointer"
                onClick={handleVideoToggleAudio}
              >
                <Box
                  as="video"
                  ref={storyVideoRef}
                  w="100%"
                  h={{ base: '210px', md: '360px', lg: '410px' }}
                  objectFit="contain"
                  autoPlay
                  loop
                  muted={isVideoMuted}
                  playsInline
                  src="/videos/intro.mp4"
                  bg="black"
                />
                <Box
                  position="absolute"
                  bottom="12px"
                  left="50%"
                  transform="translateX(-50%)"
                  px={4}
                  py={2}
                  borderRadius="full"
                  bg="rgba(0,0,0,0.6)"
                  color="white"
                  fontSize="sm"
                  fontWeight="600"
                  display="flex"
                  alignItems="center"
                  gap={2}
                  pointerEvents="none"
                >
                  <Box
                    w="8px"
                    h="8px"
                    borderRadius="full"
                    bg={isVideoMuted ? 'red.400' : 'green.400'}
                  />
                  {isVideoMuted ? '클릭하면 소리가 재생됩니다' : '소리가 재생 중입니다'}
                </Box>
              </Box>

              {/* 하단 메시지 */}
              <Box
                textAlign="center"
                maxW="700px"
                mx="auto"
                p={6}
                borderRadius="xl"
                bg="brand.50"
                border="2px solid"
                borderColor="brand.200"
              >
                <Text
                  fontSize={{ base: 'md', md: 'lg' }}
                  color="gray.700"
                  lineHeight="tall"
                  fontWeight="500"
                >
                  <Text as="span" color="brand.600" fontWeight="700">
                    토계는 땅만 사는 곳이 아니에요
                  </Text>
                  <br />
                  땅에도, 사람의 마음에도 길을 내는 사람들이 모인 곳이에요
                  <br />
                  <Text as="span" color="orange.600" fontWeight="600">
                    따뜻한 관계, 진심 어린 소통
                  </Text>
                  이 토계의 가장 큰 힘이에요
                </Text>
              </Box>
            </VStack>
          </Container>
        </Box>

        {/* 토계의 핵심 가치 섹션 */}
        <Box bg="white" py={20}>
          <Container maxW="container.xl">
            <VStack spacing={16}>
              {/* 섹션 헤더 */}
              <Box textAlign="center" maxW="800px" mx="auto">
                <Heading
                  as="h2"
                  fontSize={{ base: '3xl', md: '4xl', lg: '5xl' }}
                  fontWeight="800"
                  color="gray.800"
                  mb={6}
                >
                  토계의 핵심 가치
                </Heading>
                <Text
                  fontSize={{ base: 'lg', md: 'xl' }}
                  color="gray.600"
                  lineHeight="tall"
                >
                  토계는 땅만 사는 곳이 아닙니다
                  <br />
                  <Text as="span" color="brand.600" fontWeight="700">
                    땅에도, 사람의 마음에도 길을 내는
                  </Text>
                  <br />
                  사람들이 모인 곳입니다
                </Text>
              </Box>

              {/* 핵심 가치 카드들 */}
              <SimpleGrid columns={{ base: 1, md: 3 }} spacing={6} w="100%">
                {[
                  {
                    title: '함께하는 가족 공동체',
                    desc: '계주와 계원은 지위가 아닌 함께하는 가족입니다',
                    subDesc: '서로의 이야기를 듣고, 마음의 길부터 내어 가족처럼 소통하는 공동체',
                    color: 'brand.500',
                    image: '/images/a.png',
                  },
                  {
                    title: '따뜻한 관계와 소통',
                    desc: '진심 어린 소통이 토계의 가장 큰 힘입니다',
                    subDesc: '모든 시작은 사람의 마음에서부터, 이 믿음의 공동체가 토계를 만들어왔습니다',
                    color: 'orange.500',
                    image: '/images/b.png',
                  },
                  {
                    title: '희망을 키우는 성장',
                    desc: '작은 씨앗이 희망이 되어 자라납니다',
                    subDesc: '누군가의 손길이 닿을 때마다 조금씩 자라나며 함께 희망을 키워나갑니다',
                    color: 'brand.600',
                    image: '/images/c.png',
                  },
                ].map((item, idx) => (
                  <Box
                    key={idx}
                    p={8}
                    borderRadius="xl"
                    bg="white"
                    border="1px solid"
                    borderColor="gray.200"
                    _hover={{
                      transform: 'translateY(-4px)',
                      borderColor: item.color,
                      boxShadow: '0 12px 40px rgba(0,0,0,0.08)',
                    }}
                    transition="all 0.3s ease"
                    animation={`${fadeInUp} 0.8s ease-out ${0.2 + idx * 0.1}s both`}
                    position="relative"
                  >
                    <VStack spacing={4} align="stretch">
                      {/* 아이콘 영역 */}
                      <Box
                        w="80px"
                        h="80px"
                        borderRadius="xl"
                        bg={`${item.color}10`}
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                        mb={2}
                        p={2}
                      >
                        <Image
                          src={item.image}
                          alt={item.title}
                          w="100%"
                          h="100%"
                          objectFit="contain"
                        />
                      </Box>
                      
                      <Heading
                        as="h3"
                        fontSize="xl"
                        fontWeight="800"
                        color="gray.900"
                        lineHeight="1.3"
                      >
                        {item.title}
                      </Heading>
                      
                      <Text
                        fontSize="lg"
                        color={item.color}
                        fontWeight="700"
                        lineHeight="1.5"
                      >
                        {item.desc}
                      </Text>
                      
                      <Text
                        fontSize="sm"
                        color="gray.600"
                        lineHeight="1.6"
                        mt={2}
                      >
                        {item.subDesc}
                      </Text>
                    </VStack>
                  </Box>
                ))}
              </SimpleGrid>

              {/* 추가 메시지 */}
              <Box
                textAlign="center"
                maxW="800px"
                mx="auto"
                pt={6}
                mt={4}
              >
                <VStack spacing={4}>
                  <Box
                    w="60px"
                    h="2px"
                    bgGradient="linear(to-r, transparent, brand.500, transparent)"
                    mx="auto"
                  />
                  <Text
                    fontSize={{ base: 'lg', md: 'xl' }}
                    color="gray.700"
                    lineHeight="tall"
                    fontWeight="500"
                  >
                    우리 땅을 우리가 지키고
                    <br />
                    우리 마음을 우리가 연결하며
                    <br />
                    <Text as="span" color="brand.600" fontSize={{ base: 'xl', md: '2xl' }} fontWeight="800">
                      함께 희망을 키운다는 마음
                    </Text>
                    <br />
                    <Text as="span" color="gray.600" fontSize={{ base: 'md', md: 'lg' }}>
                      이것이 토계의 시작이었습니다
                    </Text>
                  </Text>
                </VStack>
              </Box>
            </VStack>
          </Container>
        </Box>

        {/* 통계 섹션 */}
        <Box
          ref={statsRef}
          bgGradient="linear(to-br, brand.600, brand.700)"
          py={20}
          color="white"
        >
          <Container maxW="container.xl">
            <SimpleGrid columns={{ base: 2, md: 4 }} spacing={8} textAlign="center">
              {[
                { number: 44, hasPlus: false, unit: '년', label: '노하우' },
                { number: 100, hasPlus: true, unit: '명', label: '법인대표' },
                { number: 500, hasPlus: true, unit: '명', label: '가족공동체' },
                { number: 2024, hasPlus: false, unit: '년', label: '토계 출발' },
              ].map((stat, idx) => (
                <VStack
                  key={idx}
                  spacing={2}
                  animation={`${fadeInScale} 0.8s ease-out ${0.1 * idx}s both`}
                >
                  <HStack spacing={1} justify="center">
                    <Text fontSize={{ base: '4xl', md: '5xl' }} fontWeight="900">
                      {counts[idx].toLocaleString()}
                      {stat.hasPlus && '+'}
                    </Text>
                    <Text fontSize={{ base: '2xl', md: '3xl' }} fontWeight="700">
                      {stat.unit}
                    </Text>
                  </HStack>
                  <Text fontSize="lg" opacity="0.9">
                    {stat.label}
                  </Text>
                </VStack>
              ))}
            </SimpleGrid>
          </Container>
        </Box>

        {/* 펜텔 프로젝트 소개 섹션 */}
        <Box bgGradient="linear(to-br, gray.900, brand.900)" py={20} color="white" position="relative" overflow="hidden">
          {/* 배경 장식 */}
          <Box
            position="absolute"
            top="20%"
            right="10%"
            w="300px"
            h="300px"
            borderRadius="full"
            bg="orange.400"
            opacity="0.1"
            filter="blur(60px)"
            animation={`${float} 8s ease-in-out infinite`}
          />
          <Box
            position="absolute"
            bottom="20%"
            left="10%"
            w="400px"
            h="400px"
            borderRadius="full"
            bg="brand.500"
            opacity="0.1"
            filter="blur(80px)"
            animation={`${float} 10s ease-in-out infinite reverse`}
          />

          <Container maxW="container.xl" position="relative" zIndex={1}>
            <VStack spacing={10} textAlign="center">
              <Box maxW="800px" mx="auto">
                <Heading
                  as="h2"
                  fontSize={{ base: '3xl', md: '4xl', lg: '5xl' }}
                  fontWeight="800"
                  mb={6}
                  bgGradient="linear(to-r, white, orange.200, white)"
                  bgClip="text"
                  backgroundSize="200% auto"
                  animation={`${gradient} 3s ease infinite`}
                >
                  새로운 길을 만들어갑니다
                </Heading>
                <Text fontSize={{ base: 'lg', md: 'xl' }} color="gray.300" lineHeight="tall" mb={6}>
                  우리는 이제 <Text as="span" color="orange.300" fontWeight="700">펜텔</Text>이라는
                  <br />
                  새로운 길도 만들고 있어요
                </Text>
                <Text fontSize={{ base: 'md', md: 'lg' }} color="gray.400" lineHeight="tall">
                  펜션처럼 자유롭고 호텔처럼 편안한 우리만의 쉼터를 꿈꾸는 프로젝트
                  <br />
                  햇살이 드는 정원, 아이들의 웃음, 반려동물의 행복한 꼬리 흔들림
                  <br />
                  그 모든 장면이 우리가 만드는 사람의 길, 마음의 길, 행복의 길이에요
                </Text>
              </Box>

              <HStack spacing={4} justify="center" flexWrap="wrap" pt={4}>
                <Box
                  as="button"
                  px={10}
                  py={5}
                  bg="orange.500"
                  color="white"
                  borderRadius="full"
                  fontSize="xl"
                  fontWeight="700"
                  _hover={{
                    bg: 'orange.400',
                    transform: 'translateY(-3px)',
                    boxShadow: '0 15px 40px rgba(246, 192, 25, 0.4)',
                  }}
                  _active={{
                    transform: 'translateY(0)',
                  }}
                  transition="all 0.3s ease"
                  boxShadow="0 6px 20px rgba(246, 192, 25, 0.3)"
                  onClick={() => navigate('/business-introduction')}
                >
                  사업 소개 보기
                </Box>
                <Box
                  as="button"
                  px={10}
                  py={5}
                  bg="transparent"
                  color="white"
                  borderRadius="full"
                  fontSize="xl"
                  fontWeight="700"
                  border="2px solid white"
                  _hover={{
                    bg: 'rgba(255,255,255,0.1)',
                    transform: 'translateY(-3px)',
                  }}
                  _active={{
                    transform: 'translateY(0)',
                  }}
                  transition="all 0.3s ease"
                  onClick={() => navigate('/introduction')}
                >
                  회사 소개 보기
                </Box>
              </HStack>
            </VStack>
          </Container>
        </Box>

        {/* 지역별 서비스 섹션 */}
        <Box as="section" w="100%">
          <RegionMap />
        </Box>
      </Box>

      {/* 팝업 공지 모달 */}
      {popupNotice && (
        <Modal isOpen={isPopupOpen} onClose={onPopupClose} size="lg" isCentered>
          <ModalOverlay bg="blackAlpha.600" backdropFilter="blur(4px)" />
          <ModalContent maxW={{ base: '85%', md: '480px' }} borderRadius="xl">
            <ModalHeader>
              <Heading size="md">{popupNotice.title}</Heading>
            </ModalHeader>
            <ModalCloseButton />
            <ModalBody>
              <VStack spacing={4} align="stretch">
                {/* 이미지 */}
                {popupNotice.images && popupNotice.images.length > 0 && (
                  <Box>
                    <SimpleGridModal columns={1} spacing={4}>
                      {popupNotice.images.map((imageUrl: string, index: number) => (
                        <Box
                          key={index}
                          w="100%"
                          cursor="pointer"
                          onClick={() => {
                            setSelectedImage(imageUrl)
                            onImageOpen()
                          }}
                        >
                          <Image
                            src={imageUrl}
                            alt={`팝업 공지 이미지 ${index + 1}`}
                            borderRadius="md"
                            objectFit="cover"
                            w="100%"
                            h="200px"
                            mx="auto"
                          />
                        </Box>
                      ))}
                    </SimpleGridModal>
                    <Divider mt={4} />
                  </Box>
                )}
                
                {/* 내용 */}
                <Text
                  whiteSpace="pre-wrap"
                  fontSize="md"
                  lineHeight="1.8"
                  color="gray.700"
                >
                  {popupNotice.content}
                </Text>
              </VStack>
            </ModalBody>
            <ModalFooter>
              <Button
                variant="ghost"
                mr={3}
                onClick={() => {
                  // 오늘 하루 보지 않기
                  localStorage.setItem('lastPopupNoticeId', String(popupNotice.id))
                  const tomorrow = new Date()
                  tomorrow.setDate(tomorrow.getDate() + 1)
                  localStorage.setItem('popupNoticeHideUntil', tomorrow.toISOString())
                  onPopupClose()
                }}
              >
                오늘 하루 보지 않기
              </Button>
              <Button colorScheme="brand" onClick={onPopupClose}>
                닫기
              </Button>
            </ModalFooter>
          </ModalContent>
        </Modal>
      )}

      {/* 이미지 뷰어 모달 */}
      {selectedImage && (
        <Modal isOpen={isImageOpen} onClose={() => {
          onImageClose()
          setSelectedImage(null)
        }} size="xl" isCentered>
          <ModalOverlay bg="blackAlpha.700" backdropFilter="blur(6px)" />
          <ModalContent maxW={{ base: '90%', md: '800px' }} bg="black" color="white">
            <ModalCloseButton color="white" />
            <ModalBody p={0}>
              <Image
                src={selectedImage}
                alt="확대 이미지"
                w="100%"
                h="100%"
                maxH="80vh"
                objectFit="contain"
              />
            </ModalBody>
          </ModalContent>
        </Modal>
      )}
    </DefaultLayout>
  )
}

export default Home
